import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ContentProvider } from "@/context/ContentContext";
import { api } from "@/lib/api";
import AuthCallback from "@/components/AuthCallback";
import Login from "@/pages/Login";
import Workspace from "@/components/Workspace";
import SignInGate from "@/pages/auth/SignInGate";
import ResetPassword from "@/pages/ResetPassword";
import PublicCheckIn from "@/pages/checkin/PublicCheckIn";
import PublicNameCheckIn from "@/pages/checkin/PublicNameCheckIn";
import LiveEdit from "@/components/LiveEdit";
import BannerStack from "@/components/BannerStack";

function Gate() {
  const { me, loading, fetchMe } = useAuth();
  const [gate, setGate] = useState(undefined);

  useEffect(() => {
    if (!me) { setGate(undefined); return; }
    let alive = true;
    api.get("/membership/gate")
      .then((r) => { if (alive) setGate(r.data); })
      .catch(() => { if (alive) setGate({ required: false }); });
    return () => { alive = false; };
  }, [me]);

  const spinner = (
    <div className="min-h-screen flex items-center justify-center bg-warm-cream">
      <div className="w-12 h-12 border-4 border-maroon border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (loading) return spinner;
  if (!me) return <Login />;
  if (gate === undefined) return spinner;
  if (gate.required) {
    return <SignInGate gate={gate} onResolved={async () => {
      const r = await api.get("/membership/gate");
      setGate(r.data);
      if (!r.data.required) await fetchMe();
    }} />;
  }
  return <Workspace />;
}

function AppRouter() {
  const location = useLocation();
  // Select-on-focus for number/date fields so typing REPLACES the existing value
  // instead of appending to it (e.g. a leftover "0" turning "15000" into "015000").
  useEffect(() => {
    const onFocus = (e) => {
      const el = e.target;
      if (el && el.tagName === "INPUT" && ["number", "date", "datetime-local", "time", "month"].includes(el.type)) {
        requestAnimationFrame(() => { try { el.select(); } catch (_) {} });
      }
    };
    document.addEventListener("focusin", onFocus);
    return () => document.removeEventListener("focusin", onFocus);
  }, []);
  if (location.pathname === "/checkin/names") {
    return <PublicNameCheckIn />;
  }
  if (location.pathname === "/checkin") {
    return <PublicCheckIn />;
  }
  if (location.pathname === "/reset-password") {
    return <ResetPassword />;
  }
  // Process OAuth callback FIRST, synchronously during render.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <AuthProvider>
      <LiveEdit />
      <BannerStack />
      <Gate />
    </AuthProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <ContentProvider>
        <AppRouter />
      </ContentProvider>
    </BrowserRouter>
  );
}
